import {z} from 'zod';
import {LogoURLs, TodoStatusSchema} from '../entryTodos/schema';

// 型のre-export（後方互換性のため）
export type {LogoURLsType} from '../entryTodos/schema';

// カレンダー日時表現
export const CalendarDateTime = z.object({
  dateTime: z.string().optional(), // RFC3339形式（時刻指定の場合）
  date: z.string().optional(), // YYYY-MM-DD形式（終日イベントの場合）
  timeZone: z.string().optional(), // タイムゾーン
});

// stepイベント用のprivateプロパティ
export const StepPrivateProperties = z.object({
  stepID: z.number(),
  entryID: z.number(),
  companyID: z.number(),
  companyName: z.string(),
  logoURLs: LogoURLs,
  stepOrder: z.number(),
  result: z.string().nullable(), // 選考結果（null許容）
  deadline: CalendarDateTime.optional(),
  start: CalendarDateTime.optional(),
  end: CalendarDateTime.optional(),
  location: z.string().optional(),
  memo: z.string().optional(),
});

// todoイベント用のprivateプロパティ
export const TodoPrivateProperties = z.object({
  todoID: z.number(),
  status: TodoStatusSchema,
  priority: z.number().min(1).max(3).nullable(), // 優先度（null許容）
  link: z.string(),
  companyID: z.number().optional(),
  companyName: z.string().optional(),
  logoURLs: LogoURLs.optional(),
});

// calendarイベント用のprivateプロパティ
export const CalendarPrivateProperties = z.object({
  calendarID: z.number(),
  eventID: z.number().optional(),
  eventTable: z.string().optional(),
  url: z.string().optional(),
  companyID: z.number().optional(),
  logoURLs: LogoURLs.optional(),
});

// イベントタイプ固有の拡張プロパティ
// APIはadditionalProperties: trueなので、z.passthrough()で未知のプロパティも許容
export const ExtendedProperties = z.object({
  private: z.union([
    StepPrivateProperties.passthrough(),
    TodoPrivateProperties.passthrough(),
    CalendarPrivateProperties.passthrough(),
  ]),
});

export type StepPrivatePropertiesType = z.infer<typeof StepPrivateProperties>;
export type TodoPrivatePropertiesType = z.infer<typeof TodoPrivateProperties>;
export type CalendarPrivatePropertiesType = z.infer<
  typeof CalendarPrivateProperties
>;

// カレンダーイベント
export const CalendarEvent = z.object({
  id: z.string(), // イベント一意識別子（形式: "{type}_{originalID}"）
  type: z.enum(['todo', 'step', 'calendar']), // イベントタイプ
  title: z.string(), // イベントタイトル
  memo: z.string().optional(), // メモ/詳細/備考
  location: z.string().optional(), // 実施場所
  start: CalendarDateTime, // 開始日時
  end: CalendarDateTime, // 終了日時
  deadline: CalendarDateTime.optional(), // 締切日時
  created: z.string(), // 作成日時（RFC3339形式）
  updated: z.string(), // 更新日時（RFC3339形式）
  isEditable: z.boolean().optional(), // イベントが編集可能かどうか
  extendedProperties: ExtendedProperties, // イベントタイプ固有の拡張プロパティ
});

// GET /3.0/service/users/me/entry-calendars - カレンダーイベント取得
export const GetEntryCalendarsOutput = z.object({
  kind: z.string(), // レスポンスタイプ識別子
  summary: z.string(), // カレンダー名
  timeZone: z.string(), // デフォルトタイムゾーン
  items: z.array(CalendarEvent), // カレンダーイベント一覧
  nextPageToken: z.string().optional(), // 次ページのトークン
  totalItems: z.number(), // 総イベント数
});

// クエリパラメータの型定義
export const GetEntryCalendarsQueryParams = z.object({
  timeMin: z.string().optional(), // イベント取得の開始日時（RFC3339形式）
  timeMax: z.string().optional(), // イベント取得の終了日時（RFC3339形式）
  period: z.enum(['day', 'week', 'month']).optional(), // 相対的な期間指定
  date: z.string().optional(), // 特定の日付を指定（YYYY-MM-DD形式）
  page: z.number().min(1).optional(), // ページ番号
  limit: z.number().min(1).max(500).optional(), // 1ページあたりの件数
  eventType: z.array(z.enum(['todo', 'step', 'calendar'])).optional(), // 取得するイベントタイプ
  companyID: z.number().min(1).optional(), // 特定企業でフィルタリング
  keyword: z.string().max(100).optional(), // キーワード検索（部分一致）
});

/**
 * CRUD操作時のGoogle同期結果
 * success=true: 同期成功、success=false: 同期失敗
 * フィールド自体が存在しない場合はGoogle未連携
 */
export const GoogleSyncResult = z.object({
  success: z.boolean(),
  error: z.string().optional(),
});

export type GoogleSyncResultType = z.infer<typeof GoogleSyncResult>;

// POST /3.0/service/users/me/entry-calendars - カレンダーエントリー作成
export const CreateEntryCalendarInput = z.object({
  start: z.string(), // 開始日時（RFC3339形式）
  end: z.string().optional(), // 終了日時（RFC3339形式、省略可能）
  isAllDay: z.boolean(), // 終日イベントフラグ
  title: z.string(), // イベントタイトル
  memo: z.string().optional(), // メモ・備考
  place: z.string().optional(), // 実施場所
});

export const CreateEntryCalendarOutput = z.object({
  id: z.number(), // 作成されたカレンダーエントリーID
  userID: z.number(), // ユーザーID
  start: z.string(), // 開始日時（RFC3339形式）
  end: z.string().nullable(), // 終了日時（省略可能）
  isAllDay: z.boolean(), // 終日イベントフラグ
  title: z.string(), // イベントタイトル
  memo: z.string(), // メモ・備考
  place: z.string(), // 実施場所
  created: z.string(), // 作成日時（RFC3339形式）
  modified: z.string(), // 更新日時（RFC3339形式）
  googleSyncResult: GoogleSyncResult.optional(),
});

// DELETE /3.0/service/users/me/entry-calendars/{id} - カレンダーエントリー削除
// Google連携時は200（同期結果あり）、未連携時は204 No Content
export const DeleteEntryCalendarOutput = z.object({
  googleSyncResult: GoogleSyncResult.optional(),
});

// PUT /3.0/service/users/me/entry-calendars/{id} - カレンダーエントリー更新
export const UpdateEntryCalendarInput = z.object({
  start: z.string().optional(), // 開始日時（RFC3339形式、省略可能）
  end: z.string().optional(), // 終了日時（RFC3339形式、省略可能）
  isAllDay: z.boolean().optional(), // 終日イベントフラグ（省略可能）
  title: z.string().optional(), // イベントタイトル（省略可能）
  memo: z.string().optional(), // メモ・備考（省略可能）
  place: z.string().optional(), // 実施場所（省略可能）
});

export const UpdateEntryCalendarOutput = z.object({
  id: z.number(), // カレンダーエントリーID
  userID: z.number(), // ユーザーID
  start: z.string(), // 開始日時（RFC3339形式）
  end: z.string().nullable(), // 終了日時（省略可能）
  isAllDay: z.boolean(), // 終日イベントフラグ
  title: z.string(), // イベントタイトル
  memo: z.string(), // メモ・備考
  place: z.string(), // 実施場所
  created: z.string(), // 作成日時（RFC3339形式）
  modified: z.string(), // 更新日時(RFC3339形式)
  googleSyncResult: GoogleSyncResult.optional(),
});

/**
 * GET /3.0/service/users/me/entry-calendars/export - カレンダーエクスポート
 * ICS形式のテキストデータを返す
 */
export const ExportEntryCalendarsOutput = z.string();

// ============================================================
// Google Calendar Sync
// ============================================================

/**
 * GET /3.0/service/users/me/entry-calendars/google-sync/status
 * Googleカレンダー連携状態取得
 */
export const GetEntryCalendarGoogleSyncStatusOutput = z.object({
  isConnected: z.boolean(),
  gmail: z.string().optional(),
  calendarId: z.string().optional(),
});

export type GetEntryCalendarGoogleSyncStatusType = z.infer<
  typeof GetEntryCalendarGoogleSyncStatusOutput
>;

/**
 * 同期失敗イベント情報
 */
export const SyncFailedEvent = z.object({
  icalUid: z.string(),
  title: z.string(),
  error: z.string(),
});

/**
 * POST /3.0/service/users/me/entry-calendars/google-sync
 * Googleカレンダー同期実行
 */
export const PostEntryCalendarGoogleSyncOutput = z.object({
  success: z.boolean(),
  calendarId: z.string(),
  totalCount: z.number(),
  successCount: z.number(),
  failedEvents: z.array(SyncFailedEvent).nullable(),
});

export type PostEntryCalendarGoogleSyncType = z.infer<
  typeof PostEntryCalendarGoogleSyncOutput
>;

/**
 * DELETE /3.0/service/users/me/entry-calendars/google-sync
 * Googleカレンダー連携解除
 * 成功時は204 No Content（空のボディ）
 */
export const DeleteEntryCalendarGoogleSyncOutput = z.string();

/**
 * GET /3.0/service/users/me/entry-calendars/google-sync/failures
 * 同期失敗一覧取得
 */
export const GoogleSyncFailureEvent = z.object({
  type: z.enum(['calendar', 'step', 'todo']),
  id: z.number(),
  title: z.string(),
});

export const GetGoogleSyncFailuresOutput = z.object({
  failedCount: z.number(),
  events: z.array(GoogleSyncFailureEvent),
});

/**
 * POST /3.0/service/users/me/entry-calendars/google-sync/retry
 * 失敗分再同期
 */
export const RetrySyncFailedEvent = z.object({
  type: z.string(),
  id: z.number(),
  icalUid: z.string(),
  title: z.string(),
  error: z.string(),
});

export const PostGoogleSyncRetryOutput = z.object({
  totalCount: z.number(),
  successCount: z.number(),
  failedEvents: z.array(RetrySyncFailedEvent),
});

// ============================================================
// ICS Import
// ============================================================

/**
 * スキップされたイベント情報
 */
export const ImportSkippedEvent = z.object({
  icalUid: z.string(),
  title: z.string(),
  reason: z.string(),
});

/**
 * POST /3.0/service/users/me/entry-calendars/import
 * ICSファイルインポート
 */
export const ImportEntryCalendarsInput = z.object({
  icsData: z.string(), // ICS形式のカレンダーデータ（最大10MB）
});

export const ImportEntryCalendarsOutput = z.object({
  totalCount: z.number(), // ICSファイル内の総イベント数
  importedCount: z.number(), // 新規作成されたイベント数
  updatedCount: z.number(), // 上書き更新されたイベント数
  skippedCount: z.number(), // スキップされたイベント数
  skippedEvents: z.array(ImportSkippedEvent), // スキップされたイベントの詳細
});
